import { useRef, useState } from "react";
import { API, extractErrorMessage, logError } from "./sections";

// XHR-based upload with progress (fetch has no upload progress events).
function uploadWithProgress({ url, headers, body, onProgress, xhrRef }) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        if (xhrRef) xhrRef.current = xhr;
        xhr.open("POST", url);
        Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        const started = Date.now();
        xhr.upload.onprogress = (e) => {
            if (!e.lengthComputable) return;
            const elapsed = (Date.now() - started) / 1000;
            const bps = elapsed > 0 ? e.loaded / elapsed : 0;
            const eta = bps > 0 ? (e.total - e.loaded) / bps : 0;
            onProgress?.({ loaded: e.loaded, total: e.total, pct: (e.loaded / e.total) * 100, bps, eta });
        };
        xhr.onload = () => {
            const ct = xhr.getResponseHeader("content-type") || "";
            let json;
            if (ct.includes("application/json")) {
                try {
                    json = JSON.parse(xhr.responseText);
                } catch (parseErr) {
                    logError("Risposta JSON non valida dal server:", parseErr, xhr.responseText?.slice(0, 200));
                    json = { detail: "Risposta del server non leggibile (JSON non valido)." };
                }
            } else {
                json = { detail: (xhr.responseText || "").slice(0, 300) };
            }
            resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, json });
        };
        xhr.onerror = () => reject(new Error("Errore di rete durante il caricamento."));
        xhr.ontimeout = () => reject(new Error("Timeout durante il caricamento."));
        xhr.onabort = () => reject(Object.assign(new Error("Caricamento annullato."), { aborted: true }));
        xhr.send(body);
    });
}

async function pollJob(jobId, passcode, timeoutMs = 5 * 60 * 1000) {
    const started = Date.now();
    while (true) {
        await new Promise((res) => setTimeout(res, 2000));
        const jr = await fetch(`${API}/api/hvac/job/${jobId}`, { headers: { "X-Tech-Passcode": passcode } });
        let jdata = null;
        if ((jr.headers.get("content-type") || "").includes("application/json")) {
            try {
                jdata = await jr.json();
            } catch (parseErr) {
                logError("Polling: JSON non valido", parseErr);
            }
        }
        if (!jr.ok) throw new Error(extractErrorMessage(jr.status, jdata));
        if (jdata?.status === "done") return jdata.result;
        if (jdata?.status === "error") throw new Error(jdata.error || "Errore nell'analisi.");
        if (Date.now() - started > timeoutMs) throw new Error("Timeout: l'analisi ha richiesto troppo tempo.");
    }
}

// Upload DXF+PDF, then poll the async job. Exposes phase/progress/error state.
export function useFileUpload(passcode) {
    const [busy, setBusy] = useState(false);
    const [phase, setPhase] = useState("");        // "upload" | "analyze" | ""
    const [progress, setProgress] = useState(null);
    const [err, setErr] = useState("");
    const xhrRef = useRef(null);

    const cancel = () => xhrRef.current?.abort();

    const run = async ({ dxfFile, pdfFile, debug, onResult }) => {
        if (!dxfFile || !pdfFile) { setErr("Seleziona entrambi i file (DXF + PDF)."); return; }
        if (!API) { setErr("Configurazione URL backend mancante. Contatta l'amministratore."); return; }
        setErr(""); setBusy(true); setPhase("upload");
        setProgress({ pct: 0, loaded: 0, total: dxfFile.size + pdfFile.size, bps: 0, eta: 0 });
        try {
            const fd = new FormData();
            fd.append("dxf", dxfFile);
            fd.append("pdf", pdfFile);
            fd.append("debug", debug ? "1" : "0");
            const r = await uploadWithProgress({
                url: `${API}/api/hvac/analyze`,
                headers: { "X-Tech-Passcode": passcode },
                body: fd,
                onProgress: setProgress,
                xhrRef,
            });
            if (!r.ok) throw new Error(extractErrorMessage(r.status, r.json));
            if (!r.json?.job_id) throw new Error("Risposta server non valida (job_id mancante).");
            setPhase("analyze"); setProgress(null);
            const result = await pollJob(r.json.job_id, passcode);
            onResult(result, { dxfName: dxfFile.name, pdfName: pdfFile.name });
        } catch (e) {
            if (!e?.aborted) {
                logError("HVAC analyze error:", e);
                setErr(e?.message || "Errore imprevisto durante l'analisi.");
            }
        } finally {
            xhrRef.current = null;
            setBusy(false); setPhase(""); setProgress(null);
        }
    };

    return { busy, phase, progress, err, setErr, run, cancel };
}
